import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import api, { Provider, CatalogApp, PlanEntitlement, getPlans } from '../services/api';
import { PlanEntitlements } from '../components/PlanEntitlements';

const Schema = z
  .object({
    slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/, 'DNS-kompatibel, 3–32 Zeichen'),
    name: z.string().min(1).max(120),
    // Leer = admin@<slug>.<Plattformdomain>; sonst nur eine Adresse in dieser Domain (prueft der Server).
    adminEmail: z.string().email().optional().or(z.literal('')),
    plan: z.enum(['basic', 'business', 'enterprise']),
    hostingProvider: z.enum(['yorizon', 'ionos', 'hetzner', 'united-cloud', 'custom']),
    hostingRegion: z.string().max(120).optional().or(z.literal('')),
    hostingTarget: z.string().max(500).optional().or(z.literal('')),
  })
  .refine(
    (d) => d.hostingProvider !== 'custom' || (d.hostingTarget ?? '').trim().length > 0,
    { message: 'Ziel-Endpoint ist bei „Sonstiger" erforderlich', path: ['hostingTarget'] },
  );
type Form = z.infer<typeof Schema>;

export function CreateTenantWizard() {
  const navigate = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [catalog, setCatalog] = useState<CatalogApp[]>([]);
  const [catalogErr, setCatalogErr] = useState<string | null>(null);
  const [optional, setOptional] = useState<Set<string>>(new Set());
  const [plans, setPlans] = useState<PlanEntitlement[]>([]);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: { plan: 'basic', hostingProvider: 'ionos' },
  });

  const selectedProvider = watch('hostingProvider');

  useEffect(() => {
    api.get<{ providers: Provider[] }>('/providers')
      .then((r) => setProviders(r.data.providers))
      .catch(() => setProviders([]));
    api.get<{ apps: CatalogApp[] }>('/catalog')
      .then((r) => setCatalog(r.data.apps))
      .catch(() => setCatalogErr('Service-Katalog konnte nicht geladen werden.'));
    getPlans().then(setPlans).catch(() => setPlans([]));
  }, []);

  const selectedPlan = watch('plan');

  const baseApps = useMemo(() => catalog.filter((a) => a.isBase), [catalog]);
  const optionalApps = useMemo(() => catalog.filter((a) => !a.isBase), [catalog]);
  const providerMeta = providers.find((p) => p.id === selectedProvider);

  const toggleOptional = (slug: string) => {
    setOptional((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const onSubmit = async (data: Form) => {
    setSubmitting(true); setErr(null);
    const selectedServices = [...baseApps.map((a) => a.slug), ...optional];
    try {
      const res = await api.post<{ tenant: { slug: string } }>('/tenants', {
        slug: data.slug,
        name: data.name,
        adminEmail: data.adminEmail || undefined,
        plan: data.plan,
        hostingProvider: data.hostingProvider,
        hostingRegion: data.hostingRegion || undefined,
        hostingTarget: data.hostingTarget || undefined,
        selectedServices,
      });
      navigate(`/tenants/${res.data.tenant.slug}`);
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Anlegen fehlgeschlagen');
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded shadow p-6 max-w-2xl space-y-4">
      <h2 className="text-xl font-semibold">Tenant anlegen</h2>

      <div>
        <label className="block text-sm font-medium mb-1">Slug (DNS-kompatibel)</label>
        <input {...register('slug')} className="w-full px-2 py-1 border rounded font-mono" placeholder="acme-corp" />
        {errors.slug && <p className="text-red-600 text-sm">{errors.slug.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Anzeigename</label>
        <input {...register('name')} className="w-full px-2 py-1 border rounded" placeholder="ACME Corp GmbH" />
        {errors.name && <p className="text-red-600 text-sm">{errors.name.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Admin-Email (optional)</label>
        <input type="email" {...register('adminEmail')} className="w-full px-2 py-1 border rounded" placeholder="admin@<slug>.sectepe.com" />
        <p className="text-slate-500 text-xs mt-1">
          Leer lassen = <code>admin@&lt;slug&gt;.&lt;Plattformdomain&gt;</code>, das Arbeitsplatz-Postfach des Mandanten.
          Eine eigene Angabe muss in dieser Domain liegen: die Adresse ist der Anmeldename, und nur dort
          entstehen Postfach und Webmail-Zugang. Die Kontaktadresse des Kunden gehört nicht hierher.
        </p>
        {errors.adminEmail && <p className="text-red-600 text-sm">{errors.adminEmail.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Plan</label>
        <select {...register('plan')} className="w-full px-2 py-1 border rounded">
          <option value="basic">basic — Essentials, bis 100 Nutzer</option>
          <option value="business">business — bis ~150 Mitarbeiter</option>
          <option value="enterprise">enterprise — 150+ Mitarbeiter</option>
        </select>
        {plans.length > 0 && <PlanEntitlements plan={selectedPlan} plans={plans} className="mt-2" />}
      </div>

      <fieldset className="border rounded p-3">
        <legend className="text-sm font-medium px-1">Hosting-Anbieter</legend>
        <select {...register('hostingProvider')} className="w-full px-2 py-1 border rounded">
          {providers.map((p) => (
            <option key={p.id} value={p.id}>{p.name} — {p.description}</option>
          ))}
        </select>

        {providerMeta && !providerMeta.custom && providerMeta.regions.length > 0 && (
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">Region</label>
            <select {...register('hostingRegion')} className="w-full px-2 py-1 border rounded font-mono">
              <option value="">— Standard —</option>
              {providerMeta.regions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}

        {selectedProvider === 'custom' && (
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">Ziel-Endpoint / Cluster</label>
            <input
              {...register('hostingTarget')}
              className="w-full px-2 py-1 border rounded font-mono"
              placeholder="https://k8s.mein-anbieter.example:6443"
            />
            {errors.hostingTarget && <p className="text-red-600 text-sm">{errors.hostingTarget.message}</p>}
          </div>
        )}
      </fieldset>

      <fieldset className="border rounded p-3">
        <legend className="text-sm font-medium px-1">Services</legend>
        {catalogErr && <p className="text-amber-700 text-sm mb-2">{catalogErr}</p>}

        {baseApps.length > 0 && (
          <div className="mb-3">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Basis (immer enthalten)</p>
            {baseApps.map((a) => (
              <label key={a.slug} className="flex items-center gap-2 text-sm py-0.5 text-slate-600">
                <input type="checkbox" checked disabled />
                <span className="font-medium">{a.name}</span>
                <span className="text-slate-400">· {a.category}</span>
              </label>
            ))}
          </div>
        )}

        {optionalApps.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Optional</p>
            <div className="max-h-64 overflow-auto pr-1">
              {optionalApps.map((a) => (
                <label key={a.slug} className="flex items-center gap-2 text-sm py-0.5">
                  <input type="checkbox" checked={optional.has(a.slug)} onChange={() => toggleOptional(a.slug)} />
                  <span>{a.name}</span>
                  <span className="text-slate-400">· {a.category}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {catalog.length === 0 && !catalogErr && <p className="text-sm text-slate-400">Lade Katalog…</p>}
      </fieldset>

      {err && <div className="bg-red-100 text-red-700 p-2 rounded">{err}</div>}

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? 'AWX-Job startet …' : 'Anlegen'}
        </button>
        <button type="button" onClick={() => navigate('/tenants')} className="btn-secondary">Abbrechen</button>
      </div>
    </form>
  );
}
